/**
 * Applies XSS protection and formatting to URL parameter values
 * Extracted from legacy getUrlParams function for reuse in refactored implementation
 *
 * @param {string} value - Raw parameter value
 * @returns {string} - XSS-protected and formatted value
 */
export const applyXSSProtection = (val = '') => {
	/**
	 * Sanitization rules (Proposed update):
	 * 1. Remove escaped backslashes (defensive against double-encoding) -> /\\+/g
	 * 2. Strip only leading OR trailing quote runs -> /^"+|"+$/g
	 * 3. Remove stray quote immediately before a space + attribute style token (e.g. foo" onmouseover=) -> /"(?=\s+[A-Za-z_:][-\w.:]*=)/g
	 * 4. Unquote HTML/JS attribute assignment values (onclick="do()") -> /([A-Za-z_:][-\w.:]*)="([^"]*)"/g => $1=$2
	 * 5. Remove quote after digit w/ NO trailing space (7"Diagonal -> 7Diagonal) while preserving cases with a space (20" Wheel) -> /(\d)"([A-Za-z])/g
	 *
	 * Test case expectations implemented:
	 *  - 20" Wheel Package => unchanged (internal quote followed by space)
	 *  - onclick="doSomething()" => onclick=doSomething()
	 *  - "wrapped" => wrapped
	 *  - foo" onmouseover="alert(1)" => foo onmouseover=alert(1)
	 *  - 7"Diagonal => 7Diagonal
	 *  - Duramax<sup>&reg;</sup> Turbo Diesel engine => unchanged (HTML entities preserved)
	 *
	 * NOTE: This is NOT a full HTML sanitizer; it's a pragmatic cleanup for known noisy/synthetic
	 * parameter values. HTML tags and angle brackets are now preserved to support facet values
	 * with formatting markup. For broader security, upstream encoding & a vetted sanitization lib should be used.
	 */
	const raw = typeof val === 'string' ? val : String(val);
	return (
		raw
			// (1) strip backslashes
			.replace(/\\+/g, '')
			// (2) unquote attribute assignment values BEFORE trimming edge quotes (allow parens in value)
			.replace(/([A-Za-z_:][-\w.:]*)="([^"]*)"/g, '$1=$2')
			// (3) remove stray quote before space+attribute token (now only quotes not part of an attribute value remain)
			.replace(/"(?=\s+[A-Za-z_:][-\w.:]*=)/g, '')
			// (4) strip leading/trailing quotes around whole string
			.replace(/^"+|"+$/g, '')
			// (5) remove digit-letter wedged quote
			.replace(/(\d)"([A-Za-z])/g, '$1$2')
	);
};

/**
 * Processes parameterMap for initial data fetch in refactored InvData class
 * Converts string values to arrays and applies XSS protection
 *
 * @param {Object} parameterMap - Raw parameter map from requestData
 * @returns {Object} - Processed parameter map with array values and XSS protection
 */
export const processParameterMapForInitialFetch = (parameterMap = {}) => {
	// NEW IMPLEMENTATION (Refactor Plan Item #1):
	// Preserve existing array values instead of coercing them into comma-separated strings.
	// Apply XSS protection to each element directly. Single primitive values become
	// one-element arrays for consistency with downstream consumers.
	const result = {};

	Object.entries(parameterMap).forEach(([key, value]) => {
		if (Array.isArray(value)) {
			result[key] = value.map((v) => applyXSSProtection(v));
		} else if (value != null && value !== '') {
			result[key] = [applyXSSProtection(value)];
		}
	});

	return result;
};

/**
 * Merges pathParameters into parameterMap for Targeted Results Pages
 * Handles the case where facet data comes from pathParameters
 *
 * @param {Object} processedParameterMap - Already processed parameter map
 * @param {Object} pathParameters - Path parameters from requestData
 * @returns {Object} - Merged parameter map with path parameters included
 */
// CMS-internal path params that are not WIS search fields and must not be
// merged into the WIS request. `metadataLocation` (TRP pages) points to a
// CMS-internal location used only by server/metadata.js for the LOCATION label;
// the legacy flow stripped it via `ignorePathParamKey` before building the WIS
// fetch URL. Without this, it would flow through to WIS as a search parameter.
export const IGNORED_PATH_PARAM_KEYS = new Set(['metadataLocation']);

export const mergePathParametersForInitialFetch = (
	processedParameterMap = {},
	pathParameters = {}
) => {
	// NEW IMPLEMENTATION (Refactor Plan Item #2):
	// Directly iterate pathParameters, preserving arrays and sanitizing each element.
	// Avoid building a synthetic query string that collapses arrays to comma-delimited strings.
	// Skip null/undefined/empty string values; deduplicate when merging.
	const mergedParams = { ...processedParameterMap };

	Object.entries(pathParameters).forEach(([key, value]) => {
		if (IGNORED_PATH_PARAM_KEYS.has(key)) {
			return; // CMS-internal, not a WIS search field
		}
		if (
			value == null ||
			value === '' ||
			(Array.isArray(value) && value.length === 0)
		) {
			return; // ignore empty
		}
		// applyXSSProtection already coerces non-strings via String(); no need to pre-cast here.
		const valuesArray = Array.isArray(value)
			? value.map((v) => applyXSSProtection(v))
			: [applyXSSProtection(value)];

		if (mergedParams[key]) {
			mergedParams[key] = [
				...new Set([...mergedParams[key], ...valuesArray])
			];
		} else {
			mergedParams[key] = valuesArray;
		}
	});

	return mergedParams;
};

/**
 * Main utility function to prepare parameterMap for initial data fetch in InvDataRefactored
 * Combines processing and merging in one step
 *
 * @param {Object} parameterMap - Raw parameter map from requestData
 * @param {Object} pathParameters - Path parameters from requestData
 * @returns {Object} - Fully processed parameter map ready for InvDataRefactored
 */
export const prepareParameterMapForInitialFetch = (
	parameterMap = {},
	pathParameters = {}
) => {
	// Now that we accept structured objects, we no longer need to parse a raw query string.
	// Step 1: sanitize & normalize parameterMap
	const processedParameterMap =
		processParameterMapForInitialFetch(parameterMap);
	// Step 2: merge path parameters (TRP support) preserving arrays
	return mergePathParametersForInitialFetch(
		processedParameterMap,
		pathParameters
	);
};

/**
 * Builds a query string object with optional overrides
 * Extracted from InvDataRefactored for reusability
 *
 * @param {Object} urlParams - URL parameters object
 * @param {string|Array} sortBy - Sort option (can be string or array)
 * @param {number|Array} start - Start index for pagination
 * @param {Object} overrides - Optional parameter overrides
 * @returns {Object} - Query string object with sortBy and start included if applicable
 */
export const getQueryStringObj = (
	urlParams = {},
	sortBy = '',
	start = 0,
	overrides = {}
) => {
	const params = { ...urlParams };

	if (sortBy && !sortBy.includes('')) {
		params.sortBy = sortBy;
	}
	if (start !== 0) {
		params.start = start;
	}

	return {
		...params,
		...overrides
	};
};

/**
 * Converts a facet object (or primitive) into a formatted URL query string.
 * Migrated from legacy url-params.js during cleanup of dead code paths.
 *
 * @param {Object|string|number} facetObj - Facet data to serialize
 * @returns {string} - Formatted query string (e.g., "?make=Toyota&model=Camry")
 */
export const urlizeFacetObj = (facetObj) => {
	let queryString = '';

	/* eslint-disable no-constant-condition */
	switch (typeof facetObj) {
		case 'object':
			if (typeof facetObj) {
				Object.entries(facetObj).forEach(([name, values]) => {
					values.forEach((value) => {
						if (queryString) {
							queryString += '&';
						}
						queryString += `${encodeURIComponent(
							urlizeFacetObj(name)
						)}=${encodeURIComponent(value)}`;
					});
				});
				return `${queryString.length >= 1 ? '?' : ''}${queryString}`;
			} else {
				return '';
			}
		case 'string':
			return facetObj;
		case 'number':
			return String(facetObj);
		default:
			return '';
	}
	/* eslint-enable no-constant-condition */
};

/**
 * Filters out URL params that are not inventory-related.
 * Checks against appliedFilters, removes `incentiveId`, `vdpUrl`, and SB-prefixed params.
 * Migrated from legacy url-params.js during cleanup of dead code paths.
 *
 * @param {Object} urlParams - URL parameters object
 * @param {Array} appliedFilters - Currently applied filters
 * @returns {Object} - Filtered URL parameters
 */
export const filterNonInventoryParams = (
	urlParams = {},
	appliedFilters = []
) => {
	const knownList = appliedFilters.length
		? appliedFilters.map((item) => item.id)
		: [];
	/* eslint-disable no-param-reassign */
	return Object.keys(urlParams)
		.filter(
			(key) =>
				!knownList.includes(key) &&
				key !== 'incentiveId' &&
				key !== 'vdpUrl' &&
				!key.match(/^SB.*/)
		)
		.reduce((obj, key) => {
			obj[key] = urlParams[key];
			return obj;
		}, {});
};

/**
 * Finds and returns the legacy SB-prefixed param key(s) that map to a given facet name.
 * Migrated from legacy url-params.js during cleanup of dead code paths.
 *
 * @param {string} selectedFacet - The facet name to look up
 * @returns {Array} - Matching SB-prefixed key names
 */
export const findSBParamsMappingKey = (selectedFacet = '') => {
	const legacyParamsMapping = {
		SBtype: 'compositeType',
		SByear: 'year',
		SBmake: 'make',
		SBmodel: 'model',
		SBbodystyle: 'bodyStyle',
		SBtrim: 'trim',
		SBprice: 'price',
		SBmileage: 'odometer',
		SBcityMPG: 'cityMpg',
		SBhighwayMPG: 'highwayMpg',
		SBstockNumber: 'stockNumber',
		SBlinkedAccountId: 'accountId'
	};

	return Object.keys(legacyParamsMapping).filter(
		(key) => legacyParamsMapping[key] === selectedFacet
	);
};

/**
 * Builds a formatted query string from URL parameters
 * Extracted from InvDataRefactored for reusability
 *
 * @param {Object} urlParams - URL parameters object
 * @param {string|Array} sortBy - Sort option (can be string or array)
 * @param {number|Array} start - Start index for pagination
 * @param {Object} overrides - Optional parameter overrides
 * @returns {string} - Formatted query string (e.g., "?make=Toyota&model=Camry")
 */
export const getQueryString = (
	urlParams = {},
	sortBy = '',
	start = 0,
	overrides = {}
) => {
	return urlizeFacetObj(
		getQueryStringObj(urlParams, sortBy, start, overrides)
	);
};
